import { useRef, useState } from "react";
import { isValidPin, KeyCopyError } from "@timecapsule/crypto";
import { link } from "../App";
import { UNLOCK_CHOICES, UNLOCK_MINUTES } from "../keys";
import { isAborted } from "../passkey";
import { useSession } from "../session";
import { errorText, useT } from "../i18n";
import { formatTime } from "../time";

/**
 * The key bar for a passkey-protected key (4b step 2g). Locked: actions ask for the PIN when they
 * need the key (withKey), and the bar offers the explicit choice to unlock for a few minutes.
 * Unlocked: when the key will be wiped, and Lock. It is a bar, not a page, so a half-written
 * capsule survives an unlock.
 */
export function KeyBar() {
  const t = useT();
  const { keyStatus, unlockedUntil, lock } = useSession();
  if (keyStatus === "unlocked") {
    return (
      <div className="banner keybar unlocked">
        <span>{t.keyBar.unlockedUntil(unlockedUntil ? formatTime(unlockedUntil) : null)}</span>{" "}
        <button type="button" className="small" onClick={lock}>
          {t.keyBar.lockNow}
        </button>
      </div>
    );
  }
  return <UnlockForm />;
}

function UnlockForm() {
  const t = useT();
  const { unlock, lock, usedSinceLock } = useSession();
  const [pin, setPin] = useState("");
  const [minutes, setMinutes] = useState(UNLOCK_MINUTES);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    abort.current = new AbortController();
    try {
      await unlock(pin, minutes, abort.current.signal);
      setPin("");
    } catch (err) {
      if (!isAborted(err)) setError(err instanceof KeyCopyError ? t.keyBar.wrongPin : errorText(err));
    }
    abort.current = null;
    setBusy(false);
  }

  return (
    <div className="key-controls">
    <details className="key-disclosure">
      <summary>{t.keyBar.locked}</summary>
    <form className="banner keybar unlock" onSubmit={submit}>
      <span className="unlock-row">
        <input
          type="password"
          inputMode="numeric"
          autoComplete="off"
          placeholder={t.common.pin}
          aria-label={t.common.pin}
          value={pin}
          disabled={busy}
          onChange={(e) => setPin(e.target.value.trim())}
        />
        <select value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} disabled={busy} aria-label={t.keyDialog.minutes}>
          {UNLOCK_CHOICES.map((m) => (
            <option key={m} value={m}>
              {t.common.minutes(m)}
            </option>
          ))}
        </select>
        <button className="primary" type="submit" disabled={!isValidPin(pin) || busy}>
          {busy ? t.common.waitingPasskey : t.keyBar.unlockFor(minutes)}
        </button>
        {busy && (
          <button type="button" onClick={() => abort.current?.abort()}>
            {t.common.cancel}
          </button>
        )}
      </span>
      {busy && <span className="unlock-hint small muted">{t.common.noWindowHint}</span>}
      {error && (
        <span className="unlock-hint error">
          {error}{" "}
          <a href="/restore" onClick={link("/restore")}>
            {t.common.forgotPin}
          </a>
        </span>
      )}
    </form>
    </details>
    {usedSinceLock && <button type="button" className="small" onClick={lock} title={t.keyBar.lockTitle}>{t.keyBar.lock}</button>}
    </div>
  );
}
